import { DatePicker, Select } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetCompanySales } from '../../../hooks/graphql/useCompanySales';
import useGetAssociatedCompanies from '../../../hooks/graphql/useGetAssociatedCompanies';
import { useGetCompanySalesMetric } from '../../../hooks/graphql/useGetCompanySalesMetric';
import AnalyticsChartSection from '../analytics-chart-section/AnalyticsChartSection';
import AnalyticsSalesTable from '../analytics-sales-table/AnalyticsSalesTable';
import './AnalyticsSection.scss';

import { useTolgee, useTranslate } from '@tolgee/react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import useIsMobile from '../../../hooks/utils/useIsMobile';
import { isoStringToLocalDate } from '../../../utils/calendarUtility';
import AnalyticsDownload from '../analytics-download/AnalyticsDownload';
import HeaderBar from '../analytics-sales-table/HeaderBar';
import PrintButton from './PrintButton';

const { RangePicker } = DatePicker;

const AnalyticsSection: React.FC = () => {
  const { t } = useTranslate();
  const { getLanguage } = useTolgee();

  const language = getLanguage() || 'fr';
  const printRef = useRef<HTMLDivElement>(null);
  let startMontDate = dayjs().startOf('month').toISOString();
  const currentDate = dayjs().toISOString();

  const { associatedCompanies, getAssociatedCompanies } =
    useGetAssociatedCompanies();

  const [associatedCompaniesValue, setAssociatedCompaniesValue] = useState<
    string[]
  >([]);
  const [associatedCompaniesIds, setAssociatedCompaniesIds] = useState<
    number[]
  >([]);

  const [selectedDates, setSelectedDates] = useState<[string, string]>([
    dayjs().startOf('month').toISOString(),
    dayjs().toISOString(),
  ]);

  const salesData1 = useGetCompanySales({ metric: 1 });
  const salesData2 = useGetCompanySales({ metric: 2 });

  const salesMetric3 = useGetCompanySalesMetric({ metric: 3 });
  const salesMetric4 = useGetCompanySalesMetric({ metric: 4 });
  const salesMetric5 = useGetCompanySalesMetric({ metric: 5 });
  const salesMetric6 = useGetCompanySalesMetric({ metric: 6 });
  const salesMetric7 = useGetCompanySalesMetric({ metric: 7 });
  const salesMetric8 = useGetCompanySalesMetric({ metric: 8 });
  const salesMetric9 = useGetCompanySalesMetric({ metric: 9 });
  const salesMetric10 = useGetCompanySalesMetric({ metric: 10 });

  const isMobile = useIsMobile();

  const salesMetrics = [
    salesMetric3,
    salesMetric4,
    salesMetric5,
    salesMetric6,
    salesMetric7,
    salesMetric8,
    salesMetric9,
    salesMetric10,
  ];

  const handleDateChange = useCallback(
    (dates: any) => {
      if (dates && dates[0] && dates[1]) {
        const startDate = dates[0].startOf('day').toISOString();
        const endDate = dates[1].endOf('day').toISOString();
        setSelectedDates([startDate, endDate]); // ✅ update state
        fetchAllData(associatedCompaniesIds, startDate, endDate);
      }
    },
    [associatedCompaniesIds],
  );

  useEffect(() => {
    getAssociatedCompanies();
  }, [getAssociatedCompanies]);

  useEffect(() => {
    if (associatedCompanies) {
      const companyIds = associatedCompanies.map(
        (company: any) => company.companyId,
      );
      setAssociatedCompaniesValue(
        associatedCompanies.map((compa: any) => compa.abbreviatedCompanyName),
      );
      setAssociatedCompaniesIds(companyIds);
      fetchAllData(companyIds);
    }
  }, [associatedCompanies]);

  useEffect(() => {
    if (associatedCompaniesIds) {
      fetchAllData(associatedCompaniesIds);
    }
  }, [associatedCompaniesIds]);

  const handleAssociatedCompanyChange = (eventValue: string[]) => {
    setAssociatedCompaniesValue(eventValue);
    setAssociatedCompaniesIds(getCompanyIds(eventValue));
  };

  const getCompanyIds = (companyValues: string[]) => {
    return associatedCompanies
      .filter((comp: any) =>
        companyValues.includes(comp.abbreviatedCompanyName),
      )
      .map((com: any) => com.companyId);
  };

  const fetchAllData = useCallback(
    (companyIds: number[], startDate?: string, endDate?: string) => {
      const [savedStart, savedEnd] = selectedDates;

      const rangeStart = startDate ?? savedStart;
      const rangeEnd = endDate ?? savedEnd;

      const dateRange = {
        startDate: isoStringToLocalDate(rangeStart),
        endDate: isoStringToLocalDate(rangeEnd),
      };

      if (startDate && endDate) {
        setSelectedDates([startDate, endDate]);
      }

      salesData1.fetchCompanySales({
        filter: { fridgeCompanyIds: companyIds, saleDateRange: dateRange },
        pagination: { page: null, limit: null },
      });

      salesData2.fetchCompanySales({
        filter: { fridgeCompanyIds: companyIds, saleDateRange: dateRange },
        pagination: { page: null, limit: null },
      });

      salesMetrics.forEach(salesMetric => {
        salesMetric.fetchMetricData({
          filter: { fridgeCompanyIds: companyIds, saleDateRange: dateRange },
          pagination: { page: null, limit: null },
        });
      });
    },
    [salesData1, salesData2, salesMetrics, selectedDates],
  );

  const formatDateRange = (
    startDateStr: string,
    endDateStr: string,
    locale: string,
  ) => {
    const startDate = dayjs(startDateStr).locale(locale);
    const endDate = dayjs(endDateStr).locale(locale);

    const formattedStartDate = startDate.format('DD MMM, YYYY');
    const formattedEndDate = endDate.format('DD MMM, YYYY');

    return `${formattedStartDate} - ${formattedEndDate}`;
  };

  const formatCurrentDateRange = (dateStr: string, locale: string) => {
    return dayjs(dateStr).locale(locale).format('DD MMM, YYYY');
  };

  const formattedReportDate = useMemo(() => {
    return formatDateRange(selectedDates[0], selectedDates[1], language);
  }, [selectedDates, language]);

  const formattedCurrentDate = useMemo(() => {
    return formatCurrentDateRange(currentDate, language);
  }, [currentDate, language]);

  return (
    <div className='analytics-section-container'>
      <div className='analytics-section-header'>
        <div className='analytics-section-header-date'>
          <RangePicker
            onChange={handleDateChange}
            format='DD-MM-YYYY'
            style={{ width: '100%' }}
            defaultValue={[dayjs(startMontDate), dayjs(currentDate)]}
            placement='bottomLeft'
            dropdownClassName={classNames({
              'vertical-range-picker': isMobile,
            })}
          />
          <Select
            mode='multiple'
            maxTagCount='responsive'
            className='user-list-select-bar'
            value={associatedCompaniesValue}
            showSearch
            placeholder={t('select-a-company')}
            filterOption={(input, option) =>
              (option?.abbreviatedCompanyName ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={associatedCompanies}
            fieldNames={{
              label: 'companyName',
              value: 'abbreviatedCompanyName',
            }}
            onChange={handleAssociatedCompanyChange}
          />
        </div>

        {!isMobile && (
          <PrintButton
            printRef={printRef}
            buttonText={t('download')}
            documentTitle='Sales Report'
            type='primary'
            shape='round'
            loading={salesData1?.loading}
          />
        )}
      </div>

      <div ref={printRef} className='print-content'>
        <AnalyticsDownload
          reportDate={formattedCurrentDate}
          period={formattedReportDate}
          logoAlt='FeelEat Logo'
          logoSrc='/path-to-logo.png'
          personImageAlt='Person with phone'
          fridgeImageAlt='FeelEat fridge'
        />
        <span className='printPageBreak'></span>

        <HeaderBar companyNames={associatedCompaniesValue} />

        <AnalyticsSalesTable
          table1={salesData1?.companySales?.list ?? []}
          table1Loading={salesData1.loading}
          table2Loading={salesData2.loading}
          table2={salesData2?.companySales?.list ?? []}
        />
        <span className='printPageBreak'></span>
        <HeaderBar companyNames={associatedCompaniesValue} />

        <div className='inner-container'>
          <div
            style={{ height: '322px', overflow: 'hidden' }}
            className='col-50 float-left'
          >
            <AnalyticsChartSection
              data={salesMetrics[0].salesData?.list ?? []}
              chartType='pie'
              name={t('analytics.charts.sold-dishes')}
              type={t('analytics.charts.dishes')}
              unit='CHF'
              loading={salesMetrics[0].loading}
            />
          </div>
          <div
            style={{ height: '322px', overflow: 'hidden' }}
            className='col-50 float-right'
          >
            <AnalyticsChartSection
              data={salesMetrics[1].salesData?.list ?? []}
              chartType='bar'
              name={t('analytics.charts.average-price-dishes')}
              type={t('analytics.charts.dishes')}
              unit='CHF'
              loading={salesMetrics[1].loading}
              xAxisName={t('analytics.charts.dishes')}
            />
          </div>

          <div
            style={{ height: '322px', overflow: 'hidden' }}
            className='col-50 float-left'
          >
            <AnalyticsChartSection
              data={salesMetrics[2].salesData?.list ?? []}
              chartType='bar'
              name={t('analytics.charts.average-price-desserts')}
              type={t('analytics.charts.desserts')}
              unit='CHF'
              loading={salesMetrics[2].loading}
              xAxisName={t('deserts')}
            />
          </div>
          <div
            style={{ height: '322px', overflow: 'hidden' }}
            className='col-50 float-right'
          >
            <AnalyticsChartSection
              data={salesMetrics[3].salesData?.list ?? []}
              chartType='bar'
              name={t('analytics.charts.sold-dishes')}
              type={t('analytics.charts.dishes')}
              unit={t('analytics.charts.quantity-meals')}
              loading={salesMetrics[3].loading}
              showUnit={false}
              xAxisName={t('Sold')}
            />
          </div>
        </div>
        <span className='printPageBreak'></span>
        <HeaderBar companyNames={associatedCompaniesValue} />

        <div className='inner-container1'>
          <div style={{ height: '322px' }} className='col-50 float-left'>
            <AnalyticsChartSection
              data={salesMetrics[6].salesData?.list ?? []}
              chartType='bar'
              name={t('analytics.charts.purchase-time-meals')}
              type=''
              unit='%'
              loading={salesMetrics[6].loading}
            />
          </div>

          <div style={{ height: '322px' }} className='col-50 float-right'>
            <AnalyticsChartSection
              data={salesMetrics[7].salesData?.list ?? []}
              chartType='pie'
              name={t('Unsold/Qty delivered')}
              type=''
              unit=''
              loading={salesMetrics[7].loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsSection;
